import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { getCompanies } from "../../services/companyService.js";

/**
 * Sélecteur d'entreprise réutilisable.
 *
 * Utilisé en haut des pages PasswordsPage et UsersPage pour choisir
 * l'entreprise dont on veut gérer les données.
 *
 * Props :
 *  - selectedId   : ID de l'entreprise actuellement sélectionnée (ou null)
 *  - onChange     : callback(company) appelé quand l'utilisateur change de sélection
 *  - placeholder  : texte affiché quand rien n'est sélectionné
 */
export default function CompanySelector({ selectedId, onChange, placeholder = "Sélectionner une entreprise…" }) {
  const [companies, setCompanies] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [open,      setOpen]      = useState(false);
  const [search,    setSearch]    = useState("");

  // Référence sur le bouton trigger pour calculer la position du dropdown
  const triggerRef = useRef(null);
  // Position calculée du dropdown
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    getCompanies()
      .then(res => {
        const raw = res.data || [];

        // Déduplication : même logique que ClientsPage
        const normalize = (s) =>
          (s || "").toLowerCase()
            .replace(/[-_]/g, " ").replace(/[éèêë]/g, "e")
            .replace(/[àâä]/g, "a").replace(/[ùûü]/g, "u")
            .replace(/[îï]/g, "i").replace(/[ôö]/g, "o")
            .replace(/[ç]/g, "c").replace(/\s+/g, " ").trim();

        const seen = new Map();
        raw.forEach(c => {
          const key = normalize(c.name);
          if (!seen.has(key)) {
            seen.set(key, c);
          } else {
            const existing = seen.get(key);
            if (!existing.ateraCustomerId && c.ateraCustomerId) {
              seen.set(key, c);
            }
          }
        });

        setCompanies([...seen.values()]);
      })
      .catch(err => console.error("Erreur chargement companies :", err))
      .finally(() => setLoading(false));
  }, []);

  // Recalcule la position chaque fois qu'on ouvre le dropdown
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top:   rect.bottom + window.scrollY + 6,
        left:  rect.left   + window.scrollX,
        width: rect.width,
      });
    }
  }, [open]);

  const selected = companies.find(c => c.id === selectedId);
  const filtered = companies.filter(c =>
    (c.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (company) => {
    onChange(company);
    setOpen(false);
    setSearch("");
  };

  // Le dropdown est rendu dans document.body via un portal
  // → il n'est jamais bloqué par un parent avec overflow:hidden ou z-index bas
  const dropdown = open && createPortal(
    <>
      {/* Overlay pour fermer en cliquant ailleurs */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 9998 }}
        onClick={() => { setOpen(false); setSearch(""); }}
      />

      <div
        className="cs-dropdown"
        style={{
          position: "absolute",
          top:      dropdownPos.top,
          left:     dropdownPos.left,
          width:    dropdownPos.width,
          zIndex:   9999,
        }}
      >
        {/* Recherche */}
        <div className="cs-search-wrap">
          <i className="bi bi-search cs-search-icon"/>
          <input
            className="cs-search"
            placeholder="Rechercher une entreprise…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {/* Options */}
        <div className="cs-list">
          {filtered.length === 0 && (
            <div className="cs-empty">Aucune entreprise trouvée</div>
          )}
          {filtered.map(company => (
            <button
              key={company.id}
              className={`cs-option ${company.id === selectedId ? "cs-option-active" : ""}`}
              onClick={() => handleSelect(company)}
            >
              <div className="cs-avatar cs-avatar-sm">{company.name?.[0]}</div>
              <div className="cs-option-info">
                <span className="cs-option-name">{company.name}</span>
                {company.email && (
                  <span className="cs-option-sub">{company.email}</span>
                )}
              </div>
              {!company.active && <span className="cs-inactive-badge">Inactif</span>}
              {company.id === selectedId && (
                <i className="bi bi-check2" style={{ color: "var(--accent)", marginLeft: "auto" }}/>
              )}
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <div className="cs-wrap" ref={triggerRef}>

      {/* Bouton d'ouverture */}
      <button
        className={`cs-trigger ${open ? "cs-open" : ""}`}
        onClick={() => setOpen(o => !o)}
        disabled={loading}
      >
        {loading ? (
          <><i className="bi bi-arrow-repeat spin me-2"/>Chargement…</>
        ) : selected ? (
          <>
            <div className="cs-avatar">{selected.name?.[0]}</div>
            <span className="cs-selected-name">{selected.name}</span>
            {!selected.active && <span className="cs-inactive-badge">Inactif</span>}
          </>
        ) : (
          <>
            <i className="bi bi-building me-2" style={{ color: "var(--text-3)" }}/>
            <span style={{ color: "var(--text-3)" }}>{placeholder}</span>
          </>
        )}
        <i className={`bi bi-chevron-${open ? "up" : "down"} cs-chevron`}/>
      </button>

      {/* Dropdown rendu dans body via portal */}
      {dropdown}

      <style>{CSS}</style>
    </div>
  );
}

const CSS = `
/* Conteneur */
.cs-wrap { position: relative; display: inline-block; min-width: 280px; }

/* Bouton déclencheur */
.cs-trigger {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 9px 14px;
  background: var(--navy-mid); border: 1px solid var(--border);
  border-radius: 10px; cursor: pointer; color: var(--text-1);
  font-size: 13px; transition: border-color .15s; text-align: left;
}
.cs-trigger:hover { border-color: var(--accent); }
.cs-trigger.cs-open { border-color: var(--accent); }
.cs-trigger:disabled { opacity: .6; cursor: not-allowed; }
.cs-chevron { margin-left: auto; color: var(--text-3); font-size: 11px; flex-shrink: 0; }
.cs-selected-name { font-weight: 600; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Avatar */
.cs-avatar {
  width: 26px; height: 26px; border-radius: 7px; flex-shrink: 0;
  background: var(--accent-dim); color: var(--accent);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; text-transform: uppercase;
}
.cs-avatar-sm { width: 30px; height: 30px; border-radius: 8px; font-size: 13px; }

/* Dropdown — rendu dans body via portal, positionné en absolute */
.cs-dropdown {
  background: var(--navy-mid);
  border: 1px solid var(--border); border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0,0,0,.5);
  overflow: hidden; animation: csIn .15s ease;
  min-width: 280px;
}
@keyframes csIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }

/* Recherche */
.cs-search-wrap {
  position: relative; padding: 10px 12px;
  border-bottom: 1px solid var(--border);
}
.cs-search-icon {
  position: absolute; left: 22px; top: 50%; transform: translateY(-50%);
  color: var(--text-3); font-size: 13px; pointer-events: none;
}
.cs-search {
  width: 100%; background: rgba(255,255,255,.05);
  border: 1px solid rgba(255,255,255,.1); border-radius: 8px;
  color: var(--text-1); font-size: 13px; padding: 7px 10px 7px 32px; outline: none;
}
.cs-search::placeholder { color: var(--text-3); }
.cs-search:focus { border-color: var(--accent); }

/* Liste */
.cs-list { max-height: 280px; overflow-y: auto; padding: 6px; }
.cs-empty { font-size: 12px; color: var(--text-3); text-align: center; padding: 20px; }

/* Option */
.cs-option {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 8px 10px; border-radius: 8px;
  background: none; border: none; cursor: pointer; text-align: left;
  transition: background .12s;
}
.cs-option:hover { background: rgba(255,255,255,.06); }
.cs-option-active { background: var(--accent-dim) !important; }
.cs-option-info { flex: 1; min-width: 0; }
.cs-option-name { display: block; font-size: 13px; font-weight: 500; color: var(--text-1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cs-option-sub  { display: block; font-size: 11px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Badge inactif */
.cs-inactive-badge {
  font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 20px;
  background: rgba(239,68,68,.1); color: var(--danger);
  border: 1px solid rgba(239,68,68,.2); flex-shrink: 0;
}

@keyframes spin { to { transform: rotate(360deg); } }
.spin { animation: spin .7s linear infinite; display: inline-block; }
`;